import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Lock,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  History as HistoryIcon,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/shared/store/page';
import { useAuditList, useVerifyAuditChain, type VerifyChainResult } from './api';

const DEFAULT_PAGE_SIZE = 20;

const moduleVariant: Record<string, 'gray' | 'blue' | 'purple' | 'amber' | 'green' | 'teal'> = {
  Sistema: 'gray',
  Pacientes: 'blue',
  Procedimientos: 'teal',
  Consentimiento: 'blue',
  Anestesia: 'purple',
  'Hoja de gasto': 'amber',
  Usuarios: 'green',
  Configuración: 'gray',
  Reportes: 'gray',
};

interface VerifyState {
  /** Resultado de la última verificación + timestamp local. */
  result: VerifyChainResult;
  at: Date;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-CO', {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}

export function AuditLogPage(): JSX.Element {
  usePageTitle(
    'Auditoría',
    'Bitácora inmutable con hash-chain — append-only por trigger SQL',
  );

  const [page, setPage] = useState<number>(1);
  const [verifyState, setVerifyState] = useState<VerifyState | null>(null);

  const { data, isLoading, isFetching, isError, error, refetch } = useAuditList({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const verifyChain = useVerifyAuditChain();

  // Si la página queda vacía tras un cambio (caso edge), volvemos a la 1.
  useEffect(() => {
    if (
      data !== undefined &&
      data.items.length === 0 &&
      data.total > 0 &&
      page > 1
    ) {
      setPage(1);
    }
  }, [data, page]);

  const handleVerify = async (): Promise<void> => {
    try {
      const result = await verifyChain.mutateAsync();
      setVerifyState({ result, at: new Date() });
    } catch {
      // Errores HTTP los expone `verifyChain.error` automáticamente.
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = total === 0 ? 1 : Math.ceil(total / DEFAULT_PAGE_SIZE);

  return (
    <div className="flex flex-col gap-4">
      {/* Banner informativo */}
      <Card className="bg-primary-soft border-primary/20">
        <div className="p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-text-soft flex-1">
            <div className="font-semibold text-text mb-0.5">Garantías criptográficas</div>
            <div>
              Cada evento se encadena con SHA-256 al evento anterior (canonical JSON). Los triggers
              SQL bloquean <code className="text-xs bg-surface px-1 rounded">UPDATE</code>/
              <code className="text-xs bg-surface px-1 rounded">DELETE</code> en{' '}
              <code className="text-xs bg-surface px-1 rounded">audit_log</code>. Cualquier
              manipulación retroactiva invalida la cadena y la detecta el endpoint{' '}
              <code className="text-xs bg-surface px-1 rounded">GET /api/audit/verify</code>.
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => void handleVerify()}
            disabled={verifyChain.isPending}
          >
            {verifyChain.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Verificando…
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" /> Verificar integridad
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Resultado de verificación */}
      {verifyChain.isError && (
        <div className="bg-danger-soft border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">No fue posible verificar la cadena</div>
            <div className="text-sm">{verifyChain.error.message}</div>
          </div>
        </div>
      )}
      {verifyState !== null && (
        <VerifyResultBanner state={verifyState} />
      )}

      {/* Error de listado */}
      {isError && (
        <div className="bg-danger-soft border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">No fue posible cargar los eventos</div>
            <div className="text-sm text-red-700">{error.message}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            <RefreshCw className="w-4 h-4" /> Reintentar
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div>
            <CardTitle>Eventos recientes</CardTitle>
            <CardDescription>
              {isLoading
                ? 'Cargando bitácora…'
                : `${total} ${total === 1 ? 'evento' : 'eventos'} registrados`}
              {isFetching && !isLoading ? ' · actualizando…' : ''}
            </CardDescription>
          </div>
        </CardHeader>

        {isLoading ? (
          <ListSkeleton />
        ) : !isError && items.length === 0 ? (
          <EmptyState />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>Módulo</TableHead>
                <TableHead>Acción</TableHead>
                <TableHead>Entidad</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="stagger-children">
              {items.map((e) => (
                <TableRow
                  key={e.id}
                  className="animate-fade-in-up hover:bg-surface-alt transition-colors"
                >
                  <TableCell>
                    <span className="text-text-muted text-xs whitespace-nowrap">
                      {formatDate(e.fecha)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium">{e.usuarioNombre}</span>
                    <div className="text-text-muted text-[11px] font-mono">{e.usuarioId}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={moduleVariant[e.modulo] ?? 'gray'}>{e.modulo}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{e.accion}</span>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-surface-alt px-1.5 py-0.5 rounded">
                      {e.entidad}
                    </code>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-xs text-text-muted">
                      <Lock className="w-3 h-3" />
                      {e.ip ?? '—'}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!isLoading && !isError && total > 0 && (
          <CardFooter className="justify-between">
            <div className="text-text-muted text-sm">
              Página {page} de {totalPages} · {total} {total === 1 ? 'evento' : 'eventos'}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}

function VerifyResultBanner({ state }: { state: VerifyState }): JSX.Element {
  const ts = state.at.toLocaleTimeString('es-CO');
  if (state.result.ok) {
    return (
      <div className="bg-success-soft border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Cadena de auditoría íntegra</div>
          <div className="text-sm">
            Verificado a las {ts}. Todos los hashes SHA-256 enlazan correctamente.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-danger-soft border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-start gap-3">
      <ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div>
        <div className="font-semibold">Integridad comprometida</div>
        <div className="text-sm">
          Detectado a las {ts} — la cadena se rompe en el registro{' '}
          <code className="text-xs bg-surface px-1 rounded">{state.result.brokenAt}</code>.
          Reporta este incidente a seguridad inmediatamente.
        </div>
      </div>
    </div>
  );
}

function ListSkeleton(): JSX.Element {
  return (
    <div className="p-6 flex flex-col gap-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-10 rounded-md bg-surface-alt animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="p-10 text-center flex flex-col items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-surface-alt grid place-items-center text-text-muted">
        <HistoryIcon className="w-6 h-6" />
      </div>
      <div className="text-text font-semibold">Sin eventos registrados</div>
      <p className="text-text-muted text-sm max-w-sm">
        La bitácora todavía no tiene actividad. Cualquier acción auditable que ocurra en la
        plataforma quedará registrada aquí automáticamente.
      </p>
    </div>
  );
}
